/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * RightCopilot.tsx — Righ\1 \2opilot (services/operator-web-app).
 */

import { Panel, IconButton } from '@/components/ui';
import { TranscriptPanel, HintsPanel } from '@/features/copilot';
import { featureFlags } from '@/config/featureFlags';
import { useCopilotCollapsed } from '@/hooks/useLayoutPreferences';
import { useTranslation } from '@/i18n';

const ChevronLeft = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

/**
 * RightCopilot: live transcript (E6-S1), hints with card + Why (E6-S2).
 * E8-S1: Hints panel gated by featureFlags.aiHints.
 * E4-S1 T3: Collapse/expand with preference persisted in localStorage.
 */
export function RightCopilot() {
  const t = useTranslation();
  const [collapsed, setCollapsed] = useCopilotCollapsed();

  if (collapsed) {
    return (
      <aside
        className="hidden xl:flex flex-col w-12 flex-shrink-0 border-l items-center pt-2"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-bg-from)' }}
        aria-label={t('copilot.collapsed')}
      >
        <IconButton
          icon={<ChevronLeft />}
          variant="ghost"
          size="md"
          aria-label={t('copilot.expand')}
          onClick={() => setCollapsed(false)}
        />
      </aside>
    );
  }

  return (
    <aside
      className="hidden xl:flex flex-col w-80 2xl:w-96 flex-shrink-0 border-l overflow-hidden"
      style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-bg-from)' }}
      aria-label={t('copilot.label')}
    >
      <div
        className="flex items-center justify-between flex-shrink-0 px-2 py-1.5 border-b"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <span className="text-xs font-medium truncate" style={{ color: 'var(--text-muted)' }}>
          Copilot
        </span>
        <IconButton
          icon={<ChevronRight />}
          variant="ghost"
          size="sm"
          aria-label={t('copilot.collapse')}
          onClick={() => setCollapsed(true)}
        />
      </div>
      <div className="flex-1 overflow-auto p-3 space-y-3 flex flex-col min-h-0">
        <TranscriptPanel />
        {featureFlags.aiHints && (
          <Panel title={t('copilot.hints')} padding="sm">
            <HintsPanel />
          </Panel>
        )}
      </div>
    </aside>
  );
}
